import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, CalendarClock, FilePenLine, Filter, Phone, Mail, MapPin, Trash2, MessageSquare, Camera, Inbox } from 'lucide-react'; // Add Camera
import ModalEditarAviso from '../componentes/ModalEditarAviso';
import ModalEnviarDocumentos from '../componentes/ModalEnviarDocumentos';
import ModalVerImagenes from '../componentes/ModalVerImagenes'; // Add this import
import MaterialesAvisoResumen from '../componentes/MaterialesAvisoResumen';
import { getEstadoBadge } from '../utilidades/estadoUtils';
import { API_URL, actualizarAviso, eliminarAviso } from '../servicios/api';

const obtenerFechaHoraAviso = (aviso) => {
  if (aviso.fecha_cita && aviso.hora_cita) {
    return `${aviso.fecha_cita}T${aviso.hora_cita}`;
  }
  return aviso.fecha_cita || aviso.fecha_programada;
};

const AvisosDelDia = ({ refreshTrigger }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [tecnicoGlobalId, setTecnicoGlobalId] = useState(localStorage.getItem('filtro_tecnico_id') || '');
  const [avisos, setAvisos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [fechaDesde, setFechaDesde] = useState(today);
  const [fechaHasta, setFechaHasta] = useState(today);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
  const [avisoSeleccionado, setAvisoSeleccionado] = useState(null);
  const [isImagenesModalOpen, setIsImagenesModalOpen] = useState(false); // New state
  const [imagenesSeleccionadas, setImagenesSeleccionadas] = useState([]); // New state
  const [garantiaActualizandoId, setGarantiaActualizandoId] = useState(null);

  const obtenerAvisos = useCallback(async () => {
    console.log("Iniciando obtenerAvisos...");
    setCargando(true);
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      setError('No autenticado. Por favor, inicia sesión.');
      setCargando(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('estado', 'programado,pendiente,en_diagnostico,esperando_repuesto,presupuestado');
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      const usuarioActual = JSON.parse(localStorage.getItem('usuario') || 'null');
      if (usuarioActual?.rol === 'administrador' && tecnicoGlobalId) params.append('tecnico_id', tecnicoGlobalId);

      const apiUrl = `${API_URL}/obtener_avisos.php?${params.toString()}`;
      console.log("URL de la API en AvisosDelDia:", apiUrl);

      const respuesta = await fetch(apiUrl,
       {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log("Respuesta raw de la API en AvisosDelDia:", respuesta);
      const datos = await respuesta.json();
      console.log("Datos recibidos del backend en AvisosDelDia:", datos);

      // El backend ahora devuelve directamente el array de avisos, no un objeto con 'exito'
      const avisosRecibidos = datos.exito && Array.isArray(datos.avisos) ? datos.avisos : []; // Asegurarse de que sea un array
      console.log("Avisos recibidos (después de Array.isArray) en AvisosDelDia:", avisosRecibidos);

      // Fetch images for each aviso
      const avisosConImagenes = await Promise.all(avisosRecibidos.map(async (aviso) => {
        const imagenesRes = await fetch(`${API_URL}/obtener_imagenes_aviso.php?id_aviso=${aviso.id_aviso}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        const imagenesData = await imagenesRes.json();
        return { ...aviso, imagenes: imagenesData.exito ? imagenesData.imagenes : [] };
      }));
      setAvisos(avisosConImagenes);
      console.log("Estado 'avisos' actualizado en AvisosDelDia:", avisosConImagenes);
    } catch (err) {
      console.error('Error al obtener avisos:', err);
      setError('Error de conexión con el servidor.');
      setAvisos([]);
    } finally {
      setCargando(false);
    }
  }, [fechaDesde, fechaHasta, tecnicoGlobalId]);

  useEffect(() => {
    obtenerAvisos();
  }, [obtenerAvisos, refreshTrigger]); // Add refreshTrigger to dependency array

  useEffect(() => {
    const actualizarTecnicoGlobal = (event) => setTecnicoGlobalId(event.detail?.tecnicoId || '');
    window.addEventListener('filtro-tecnico-cambiado', actualizarTecnicoGlobal);
    return () => window.removeEventListener('filtro-tecnico-cambiado', actualizarTecnicoGlobal);
  }, []);

  console.log("Estado actual de avisos en el componente:", avisos);

  // Handlers for modals and updates
  const handleEditClick = (aviso) => { setAvisoSeleccionado(aviso); setIsEditModalOpen(true); };
  const handleDocsClick = (aviso) => { setAvisoSeleccionado(aviso); setIsDocsModalOpen(true); };
  const handleCloseModals = () => { setIsEditModalOpen(false); setIsDocsModalOpen(false); setAvisoSeleccionado(null); };
  const handleAvisoUpdated = () => { obtenerAvisos(); };

  const handleCompletarGarantia = async (aviso) => {
    setGarantiaActualizandoId(aviso.id_aviso);
    const datos = new FormData();
    datos.append('id_aviso', aviso.id_aviso);
    datos.append('estado', 'completado');

    try {
      const respuesta = await actualizarAviso(datos);
      if (!respuesta.exito) {
        throw new Error(respuesta.mensaje || 'No se pudo completar la garantía.');
      }
      await obtenerAvisos();
    } catch (errorActualizacion) {
      console.error('Error al completar la garantía:', errorActualizacion);
      window.alert(errorActualizacion.response?.data?.mensaje || errorActualizacion.message || 'Error al completar la garantía.');
    } finally {
      setGarantiaActualizandoId(null);
    }
  };

  const handleVerImagenesClick = (imagenes) => {
    setImagenesSeleccionadas(imagenes);
    setIsImagenesModalOpen(true);
  };

  const handleCloseImagenesModal = () => {
    setIsImagenesModalOpen(false);
    setImagenesSeleccionadas([]);
  };

  const handleDeleteClick = async (id_aviso) => {
    if (!window.confirm('¿Eliminar definitivamente este servicio? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const datos = await eliminarAviso(id_aviso);
      if (datos.exito) {
        if (datos.mensaje) window.alert(datos.mensaje);
        await obtenerAvisos();
      } else {
        window.alert(`Error al eliminar servicio: ${datos.mensaje}`);
      }
    } catch (err) {
      console.error('Error al eliminar aviso:', err);
      window.alert(err.response?.data?.mensaje || 'Error de conexión con el servidor al eliminar el servicio.');
    }
  };

  const renderTableBody = () => {
    if (cargando) return (
      <tr><td colSpan="5" className="px-4 py-14 text-center text-slate-400">
        <div className="flex flex-col items-center gap-2">
          <svg className="h-6 w-6 animate-spin text-brand-orange" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4Z" />
          </svg>
          Cargando avisos...
        </div>
      </td></tr>
    );
    if (error) return <tr><td colSpan="5" className="px-4 py-10 text-center text-red-400">Error: {error}</td></tr>;
    if (avisos.length === 0) return (
      <tr><td colSpan="5" className="px-4 py-14 text-center text-slate-400">
        <div className="flex flex-col items-center gap-2">
          <Inbox className="h-8 w-8 text-slate-600" />
          No hay avisos para el rango de fechas seleccionado.
        </div>
      </td></tr>
    );
    return avisos.map((aviso) => (
        <tr key={aviso.id_aviso} className="bg-slate-800 hover:bg-slate-700 transition-colors rounded-lg">
            <td className="px-4 py-4 align-top rounded-l-lg">
                <div className="font-medium text-slate-100">{aviso.nombre_cliente}</div>
                <div className="text-sm text-slate-400 truncate max-w-xs">
                    <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(aviso.direccion)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-blue-400 transition-colors"
                    >
                        {aviso.direccion}
                    </a>
                </div>
            </td>
            <td className="px-4 py-4 align-top">
                <div className="flex items-center gap-2 text-sm text-slate-300">
                    <a href={`tel:${aviso.telefono}`} className="hover:text-blue-400 transition-colors">
                        {aviso.telefono}
                    </a>
                    <a href={`https://wa.me/${(aviso.telefono ?? '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 transition-colors">
                        <MessageSquare className="w-4 h-4" />
                    </a>
                </div>
                <div className="text-sm text-blue-400 hover:text-blue-300 truncate max-w-xs">
                    <a href={`mailto:${aviso.email_cliente}`}>{aviso.email_cliente}</a>
                </div>
            </td>
            <td className="px-4 py-4 align-top">
                <div className="text-sm text-slate-300">
                    {new Date(obtenerFechaHoraAviso(aviso)).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    {' - '}
                    {new Date(obtenerFechaHoraAviso(aviso)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                {aviso.tipo_registro === 'garantia' && (
                  <span className="mt-1 inline-flex rounded bg-amber-400/15 px-2 py-1 text-xs font-bold text-amber-300">GARANTÍA</span>
                )}
                <div className="text-sm text-slate-400">{aviso.aparato ? `${aviso.aparato} · ` : ''}{aviso.descripcion}</div>
                <div className="text-xs text-slate-500">Técnico: {aviso.tecnico_nombre || 'Sin asignar'}</div>
                <MaterialesAvisoResumen avisoId={aviso.id_aviso} precioCobrado={aviso.precio_cobrado} />
            </td>
            <td className="px-4 py-4 align-top">
              {getEstadoBadge(aviso.estado)}
            </td>
            <td className="px-4 py-4 align-top rounded-r-lg">
                <div className="flex items-center gap-2">
                    <button className="p-1.5 text-blue-400 hover:text-white hover:bg-blue-700 rounded-md transition-colors" onClick={() => handleVerImagenesClick(aviso.imagenes)}><Camera className="w-5 h-5" /></button>
                    {aviso.tipo_registro === 'garantia' && aviso.estado === 'pendiente' && (
                      <button
                        type="button"
                        disabled={garantiaActualizandoId === aviso.id_aviso}
                        onClick={() => handleCompletarGarantia(aviso)}
                        className="rounded bg-green-700 px-2 py-1.5 text-xs font-semibold text-white hover:bg-green-600 disabled:opacity-50"
                      >
                        {garantiaActualizandoId === aviso.id_aviso ? 'Guardando...' : 'Completar Garantía'}
                      </button>
                    )}
                    <button
                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-md transition-colors"
                        onClick={() => handleEditClick(aviso)}
                    >
                        <FilePenLine className="w-5 h-5" />
                    </button>
                    <button className="p-1.5 text-red-400 hover:text-white hover:bg-red-700 rounded-md transition-colors"
                        onClick={() => handleDeleteClick(aviso.id_aviso)}
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>
                </div>
            </td>
        </tr>
    ));
  };

  const renderCardView = () => {
    if (cargando) return (
      <div className="flex flex-col items-center gap-2 text-slate-400 mt-4">
        <svg className="h-6 w-6 animate-spin text-brand-orange" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4Z" />
        </svg>
        Cargando avisos...
      </div>
    );
    if (error) return <p className="text-red-400 text-center mt-4">Error: {error}</p>;
    if (avisos.length === 0) return (
      <div className="flex flex-col items-center gap-2 text-slate-400 mt-6">
        <Inbox className="h-8 w-8 text-slate-600" />
        No hay avisos para el rango de fechas seleccionado.
      </div>
    );
    return avisos.map((aviso) => (
        <div key={aviso.id_aviso} className="bg-slate-800 p-4 rounded-lg shadow-md">
            <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-slate-100 text-lg truncate">{aviso.nombre_cliente}</h3>
                {getEstadoBadge(aviso.estado)}
            </div>
            <div className="text-sm text-white mb-1 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="truncate">
                    {new Date(obtenerFechaHoraAviso(aviso)).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    {' - '}
                    {new Date(obtenerFechaHoraAviso(aviso)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
            </div>
            {aviso.tipo_registro === 'garantia' && (
              <span className="mb-2 inline-flex rounded bg-amber-400/15 px-2 py-1 text-xs font-bold text-amber-300">GARANTÍA</span>
            )}
            <div className="text-sm text-slate-400 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-400" />
                <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(aviso.direccion)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition-colors truncate"
                >
                    {aviso.direccion}
                </a>
            </div>
            <p className="text-sm text-slate-300 mb-1 truncate">{aviso.aparato ? `${aviso.aparato} · ` : ''}{aviso.descripcion}</p>
            <p className="mb-3 text-xs text-slate-500">Técnico: {aviso.tecnico_nombre || 'Sin asignar'}</p>
            <MaterialesAvisoResumen avisoId={aviso.id_aviso} precioCobrado={aviso.precio_cobrado} />

            <div className="border-t border-slate-700 pt-3 mt-3">
                <div className="flex items-center gap-2 text-sm text-slate-300 mb-1">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${aviso.telefono}`} className="hover:text-blue-400 transition-colors truncate">
                        {aviso.telefono}
                    </a>
                    <a href={`https://wa.me/${(aviso.telefono ?? '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 transition-colors">
                        <MessageSquare className="w-4 h-4" />
                    </a>
                </div>
                <div className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${aviso.email_cliente}`} className="truncate">{aviso.email_cliente}</a>
                </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
                <button className="p-2 text-blue-400 hover:text-white hover:bg-blue-700 rounded-md transition-colors" onClick={() => handleVerImagenesClick(aviso.imagenes)}><Camera className="w-5 h-5" /></button>
                {aviso.tipo_registro === 'garantia' && aviso.estado === 'pendiente' && (
                  <button
                    type="button"
                    disabled={garantiaActualizandoId === aviso.id_aviso}
                    onClick={() => handleCompletarGarantia(aviso)}
                    className="rounded bg-green-700 px-2 py-2 text-xs font-semibold text-white hover:bg-green-600 disabled:opacity-50"
                  >
                    {garantiaActualizandoId === aviso.id_aviso ? 'Guardando...' : 'Completar Garantía'}
                  </button>
                )}
                <button
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-md transition-colors"
                    onClick={() => handleEditClick(aviso)}
                >
                    <FilePenLine className="w-5 h-5" />
                </button>
                <button className="p-2 text-red-400 hover:text-white hover:bg-red-700 rounded-md transition-colors"
                    onClick={() => handleDeleteClick(aviso.id_aviso)}
                >
                    <Trash2 className="w-5 h-5" />
                </button>
            </div>
        </div>
    ));
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 sm:p-6">
      <div className="mb-6 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-orange/15 ring-1 ring-brand-orange/30">
            <CalendarClock className="h-6 w-6 text-brand-orange" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">Avisos del día</h1>
            <p className="text-sm text-slate-400">Agenda y seguimiento de los avisos programados en el rango seleccionado.</p>
          </div>
        </div>
        {!cargando && !error && (
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-slate-700 bg-slate-800/80 px-3 py-1 text-xs font-semibold text-slate-300">
            {avisos.length} {avisos.length === 1 ? 'aviso' : 'avisos'}
          </span>
        )}
      </div>

      <div className="mb-6 rounded-xl border border-slate-700 bg-slate-900/60 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label htmlFor="fechaDesde" className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
              <Calendar className="h-3.5 w-3.5" /> Desde
            </label>
            <input type="date" id="fechaDesde" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 transition focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange" />
          </div>
          <div className="flex-1">
            <label htmlFor="fechaHasta" className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
              <Calendar className="h-3.5 w-3.5" /> Hasta
            </label>
            <input type="date" id="fechaHasta" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 transition focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange" />
          </div>
          <button onClick={obtenerAvisos} className="flex items-center justify-center gap-2 rounded-lg bg-brand-orange px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition hover:bg-opacity-90">
            <Filter className="h-4 w-4" /> Filtrar
          </button>
        </div>
      </div>

      {/* Table for larger screens */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[1000px] border-separate border-spacing-y-2">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Cliente</th>
              <th className="px-4 py-3 text-left font-semibold">Contacto</th>
              <th className="px-4 py-3 text-left font-semibold">Detalles del Aviso</th>
              <th className="px-4 py-3 text-left font-semibold">Estado</th>
              <th className="px-4 py-3 text-left font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>{renderTableBody()}</tbody>
        </table>
      </div>

      {/* Cards for smaller screens */}
      <div className="md:hidden grid grid-cols-1 gap-4 mx-auto max-w-md">
        {renderCardView()}
      </div>

      {/* Modals */}
      {avisoSeleccionado && <ModalEditarAviso isOpen={isEditModalOpen} onClose={handleCloseModals} aviso={avisoSeleccionado} onUpdateAviso={handleAvisoUpdated} />}
      {avisoSeleccionado && <ModalEnviarDocumentos isOpen={isDocsModalOpen} onClose={handleCloseModals} aviso={avisoSeleccionado} onUpdateAviso={handleAvisoUpdated} />}
      <ModalVerImagenes
        isOpen={isImagenesModalOpen}
        onClose={handleCloseImagenesModal}
        imagenes={imagenesSeleccionadas}
      />
    </div>
  );
};

export default AvisosDelDia;
